// ─────────────────────────────────────────────────────────────
// Small shared bits used by all three columns.
// ─────────────────────────────────────────────────────────────

import * as React from 'react';
import { Chip, Stack, Box, useTheme } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockIcon from '@mui/icons-material/Lock';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import type { DataType, LinkMode } from './types';
import { SYSTEM_METAKEYS } from './constants';

// ── MetaKeyChip ──────────────────────────────────────────────

export interface MetaKeyChipProps {
  value: string | null | undefined;
  dim?: boolean;
}

export function MetaKeyChip({ value, dim }: MetaKeyChipProps): JSX.Element {
  const theme = useTheme();
  if (!value) {
    return (
      <Chip
        label="no MetaKey"
        size="small"
        variant="outlined"
        sx={{
          height: 20, fontSize: 10.5, fontStyle: 'italic',
          borderStyle: 'dashed',
          color: 'text.disabled',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  }
  const reserved = SYSTEM_METAKEYS.has(value);
  return (
    <Chip
      label={value}
      size="small"
      icon={reserved ? <LockIcon sx={{ fontSize: 12 }} /> : undefined}
      sx={{
        height: 20, fontSize: 10.5, fontWeight: 600,
        fontFamily: theme.typography.fontFamily?.includes('Mono') ? undefined : 'JetBrains Mono, monospace',
        bgcolor: dim ? 'action.hover' : 'primary.50',
        color: dim ? 'text.secondary' : 'primary.dark',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

// ── ModeBadge ────────────────────────────────────────────────

export interface ModeBadgeProps { mode: LinkMode; }

export function ModeBadge({ mode }: ModeBadgeProps): JSX.Element {
  const cfg: Record<LinkMode, { color: 'success' | 'error' | 'secondary'; label: string; icon: JSX.Element }> = {
    populate: { color: 'success', label: 'Populate', icon: <CalculateIcon sx={{ fontSize: 12 }} /> },
    remove:   { color: 'error',   label: 'Remove',   icon: <DeleteIcon sx={{ fontSize: 12 }} /> },
    formula:  { color: 'secondary', label: 'Formula', icon: <AutoFixHighIcon sx={{ fontSize: 12 }} /> },
    switchPlan: { color: 'secondary', label: 'Switch Plan', icon: <SwapHorizIcon sx={{ fontSize: 12 }} /> },
  };
  const c = cfg[mode];
  return (
    <Chip
      label={c.label}
      icon={c.icon}
      color={c.color}
      size="small"
      sx={{
        height: 20, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

// ── TypeIcon ─────────────────────────────────────────────────

const TYPE_ICONS: Record<DataType, JSX.Element> = {
  text:         <TextFieldsIcon sx={{ fontSize: 14 }} />,
  integer:      <NumbersIcon sx={{ fontSize: 14 }} />,
  float:        <NumbersIcon sx={{ fontSize: 14 }} />,
  boolean:      <ToggleOnIcon sx={{ fontSize: 14 }} />,
  singleselect: <RadioButtonCheckedIcon sx={{ fontSize: 14 }} />,
  multiselect:  <CheckBoxIcon sx={{ fontSize: 14 }} />,
};

export function TypeIcon({ type }: { type: DataType }): JSX.Element {
  return (
    <Box component="span" sx={{ color: 'text.secondary', display: 'inline-flex' }}>
      {TYPE_ICONS[type] ?? TYPE_ICONS.text}
    </Box>
  );
}

// ── LinkSummary — small icon + count pair ────────────────────

export function LinkCount({ count }: { count: number }): JSX.Element | null {
  if (!count) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center"
      sx={{ color: 'success.main', fontSize: 10.5, fontWeight: 600 }}>
      <LinkIcon sx={{ fontSize: 12 }} />
      <span>{count} link{count === 1 ? '' : 's'}</span>
    </Stack>
  );
}
